import EdgeSource from "Areas/SharedFeatures/types/edgeSource";
import { uuid7 } from "Common/uuid7";
import debounce from "lodash/debounce";
import { IEdge, ILabel, INode, MouseButtons } from "yfiles";
import buildItems from "../contextMenu/buildItems";
import morphLayout from "../morphLayout";
import buildChangeEdgesCallback from "./buildChangeEdgesCallback";
import buildChangeNodesCallback from "./buildChangeNodesCallback";
export default (graphComponentRef, inputMode, graphReadyRef, layoutAlgorithmRef, nodeIdsPinnedOutOfGroupRef, activeIconWithClickStopPropagationRef, contextMenuRef, documentId, setGraphInProgress, onPasteToDocumentGraphData, onSetHasPasteData, calculateMethodsSubMenu, onChangeGraphModel, onChangeZoom, onChangeSelected, onOpenTableDetails) => {
    var _a, _b, _c;
    if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) || !documentId)
        return;
    let rightButtonClickPoint = null;
    let isCreationEdgeInProgress = false;
    graphComponentRef.current.addMouseMoveListener((_, evt) => {
        var _a;
        if (activeIconWithClickStopPropagationRef.current) {
            (_a = evt.originalEvent) === null || _a === void 0 ? void 0 : _a.stopPropagation();
        }
    });
    graphComponentRef.current.addMouseDownListener((_, evt) => {
        var _a;
        ((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) && contextMenuRef.current.hide();
        if (evt.buttons === MouseButtons.RIGHT) {
            rightButtonClickPoint = evt.location;
        }
    });
    graphComponentRef.current.addMouseUpListener(async (graphComponent, evt) => {
        if (isCreationEdgeInProgress) {
            isCreationEdgeInProgress = Boolean(inputMode === null || inputMode === void 0 ? void 0 : inputMode.createEdgeInputMode.isCreationInProgress);
            return;
        }
        isCreationEdgeInProgress = Boolean(inputMode === null || inputMode === void 0 ? void 0 : inputMode.createEdgeInputMode.isCreationInProgress);
        const contextMenu = contextMenuRef.current;
        if (!graphComponent || !contextMenu || !contextMenu.setMenuItems || !contextMenu.show) {
            rightButtonClickPoint = null;
            return;
        }
        if (evt.changedButtons === MouseButtons.RIGHT &&
            rightButtonClickPoint &&
            Math.abs(rightButtonClickPoint.x - evt.location.x) < 2 &&
            Math.abs(rightButtonClickPoint.y - evt.location.y) < 2) {
            rightButtonClickPoint = null;
            const underPointerCanvas = graphComponent.getCanvasObject(evt.location);
            const underPointerItem = underPointerCanvas
                ? graphComponent.graphModelManager.getModelItem(underPointerCanvas)
                : null;
            const cursorOnEmptySpace = !underPointerItem;
            const { selectedNodes } = graphComponent.selection;
            const noSelected = selectedNodes.size === 0;
            if (noSelected ||
                cursorOnEmptySpace ||
                (!noSelected &&
                    underPointerItem instanceof ILabel &&
                    underPointerItem.owner instanceof INode &&
                    selectedNodes.includes(underPointerItem.owner))) {
                const menuItems = await buildItems(graphComponentRef, cursorOnEmptySpace, documentId, calculateMethodsSubMenu, onPasteToDocumentGraphData, onSetHasPasteData, onOpenTableDetails);
                if (menuItems && menuItems.length) {
                    const location = graphComponent.toPageFromView(graphComponent.toViewCoordinates(evt.location));
                    contextMenu.setMenuItems(menuItems);
                    contextMenu.show(location.x, location.y);
                }
            }
        }
    });
    graphComponentRef.current.graph.addNodeRemovedListener((sender, evt) => {
        if (evt.item instanceof INode) {
            const node = evt.item;
            node.labels.toArray().forEach(label => {
                sender.remove(label);
            });
        }
    });
    graphComponentRef.current.graph.addEdgeCreatedListener((_, evt) => {
        var _a, _b, _c, _d;
        if (evt.item instanceof IEdge) {
            const edge = evt.item;
            if (!edge.tag && ((_b = (_a = edge.sourceNode) === null || _a === void 0 ? void 0 : _a.tag) === null || _b === void 0 ? void 0 : _b.NodeId) && ((_d = (_c = edge.targetNode) === null || _c === void 0 ? void 0 : _c.tag) === null || _d === void 0 ? void 0 : _d.NodeId)) {
                edge.tag = new EdgeSource(uuid7(), edge.sourceNode.tag.NodeId, edge.targetNode.tag.NodeId);
                edge.sourceNode.tag.OutputLinksCount =
                    (edge.sourceNode.tag.OutputLinksCount || 0) + 1;
                edge.targetNode.tag.InputLinksCount =
                    (edge.targetNode.tag.InputLinksCount || 0) + 1;
            }
        }
    });
    graphComponentRef.current.graph.addEdgeRemovedListener((sender, evt) => {
        if (evt.item instanceof IEdge && evt.sourcePortOwner && evt.targetPortOwner instanceof INode) {
            const sourceNode = sender.nodes.find(n => n === evt.sourcePortOwner);
            const targetNode = sender.nodes.find(n => n === evt.targetPortOwner);
            if (sourceNode) {
                sourceNode.tag.OutputLinksCount =
                    (sourceNode.tag.OutputLinksCount || 1) - 1;
            }
            if (targetNode) {
                targetNode.tag.InputLinksCount = (targetNode.tag.InputLinksCount || 1) - 1;
            }
        }
    });
    if (onChangeSelected) {
        const itemSelectionChangedListener = debounce((sender) => {
            if (!onChangeSelected)
                return;
            setGraphInProgress(true);
            onChangeSelected(documentId, graphComponentRef, sender.selectedNodes.toArray(), sender.selectedEdges.toArray(), nodeIdsPinnedOutOfGroupRef.current, () => {
                setGraphInProgress(false);
            }, () => {
                setGraphInProgress(false);
            });
        }, 10);
        graphComponentRef.current.selection.addItemSelectionChangedListener(itemSelectionChangedListener);
    }
    if (onChangeZoom)
        (_c = (_b = graphComponentRef.current.canvasContext) === null || _b === void 0 ? void 0 : _b.canvasComponent) === null || _c === void 0 ? void 0 : _c.addZoomChangedListener((sender) => {
            onChangeZoom && onChangeZoom(sender.zoom);
        });
    if (onChangeGraphModel) {
        const changeNodeSourceListener = buildChangeNodesCallback(documentId, graphReadyRef, onChangeGraphModel, 100, true, false);
        graphComponentRef.current.graph.addNodeCreatedListener(changeNodeSourceListener);
        graphComponentRef.current.graph.addNodeRemovedListener(changeNodeSourceListener);
        const changeNodeSourceTagsListener = buildChangeNodesCallback(documentId, graphReadyRef, onChangeGraphModel, 50, false, true);
        graphComponentRef.current.graph.addNodeTagChangedListener(changeNodeSourceTagsListener);
        const changeEdgeSourceListener = buildChangeEdgesCallback(documentId, graphReadyRef, onChangeGraphModel, 100);
        graphComponentRef.current.graph.addEdgeCreatedListener(changeEdgeSourceListener);
        graphComponentRef.current.graph.addEdgeRemovedListener(changeEdgeSourceListener);
        const changeEdgeSourceTagsListener = buildChangeEdgesCallback(documentId, graphReadyRef, onChangeGraphModel, 50);
        graphComponentRef.current.graph.addEdgeTagChangedListener(changeEdgeSourceTagsListener);
    }
    if (inputMode === null || inputMode === void 0 ? void 0 : inputMode.moveInputMode) {
        inputMode.moveInputMode.addDragFinishedListener(() => {
            var _a, _b;
            if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.selection) || !((_b = graphComponentRef.current) === null || _b === void 0 ? void 0 : _b.graph))
                return;
            const edges = new Set(graphComponentRef.current.graph.edges);
            if (edges.size > 0) {
                morphLayout(graphComponentRef, layoutAlgorithmRef, null, edges);
            }
        });
    }
};
